import BaseButton from 'components/ui/BaseButton';
import EmailInput from 'components/common/inputs/EmailInput';
import PasswordInput from 'dashboard/auth/password-input';
import { AUTH_ACTION_TYPE, PASSWORD_INPUT_MODE } from 'enums';
import { AUTH_SCREEN } from 'constants/general';
import { t } from 'services/i18n.service';
import { useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { register } from 'services/auth.service';
import { getPasswordConditions } from 'utils/validators';
import { AuthPageContext } from '../AuthActionsWrapper';
import PrivacyConsentControl from './PrivacyConsentControl';
import { updateIsNewUserSigninProcess } from 'store/reducer/setting/slice';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import SignInUpWrapper from '../SignInUpWrapper';
import { onboardFlowSignInSelector } from 'store/reducer/onboard-flow/selectors';
import { BaseButtonLg } from 'components/ui/BaseButton';
import Mail from 'assets/icons/Mail';
import colors from 'constants/color';
import { ATTRIB } from 'constants/test';
import {
  captureException,
  capturePostHogEvent
} from 'services/posthog.service';
import { POST_HOG_EVENT } from 'constants/events';

const SignUp = () => {
  const appDispatch = useAppDispatch();
  const {
    state: {
      screen,
      email,
      isProcessing,
      isValidEmail,
      newPassword,
      hasAcceptedTerms,
      googleLoginInProgress
    },
    dispatch
  } = useContext(AuthPageContext);
  const isNewUserSigninProcess = useAppSelectorShallow(
    onboardFlowSignInSelector
  );
  const [uiState, setUiState] = useState({
    hasTocError: false,
    showWelcomeUI: true,
    hasAttemptedSubmit: false
  });

  const isSelected = useMemo(() => screen === AUTH_SCREEN.SIGN_UP, [screen]);

  useEffect(() => {
    if (isSelected) {
      capturePostHogEvent(POST_HOG_EVENT.SIGN_UP.VIEWED);
    }
  }, [isSelected]);

  const isValidPassword = useMemo(
    () =>
      getPasswordConditions(newPassword).every((condition) => condition.valid),
    [newPassword]
  );

  const isAnyAuthActionInProgress = useMemo(
    () => isProcessing || googleLoginInProgress || isNewUserSigninProcess,
    [isProcessing, googleLoginInProgress, isNewUserSigninProcess]
  );

  const handleSubmit = async () => {
    setUiState((prev) => ({ ...prev, hasAttemptedSubmit: true }));

    if (!isValidEmail || !isValidPassword || !hasAcceptedTerms) {
      if (!hasAcceptedTerms) {
        setUiState((prev) => ({ ...prev, hasTocError: true }));
        capturePostHogEvent(POST_HOG_EVENT.SIGN_UP.TERMS_NOT_ACCEPTED);
      }
      return;
    }

    if (hasAcceptedTerms) {
      dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_IS_PROCESSING, value: true });
      await register(email, newPassword, (hasError, error) => {
        dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_IS_PROCESSING });
        if (hasError) {
          toast.error(error);
          captureException(error);
          capturePostHogEvent(POST_HOG_EVENT.SIGN_UP.ERROR, {
            message: error
          });
        } else {
          appDispatch(updateIsNewUserSigninProcess(true));
          dispatch?.({ type: AUTH_ACTION_TYPE.REGISTRATION_COMPLETED });
          dispatch?.({ type: AUTH_ACTION_TYPE.NAVIGATE_TO_SIGN_IN });
          capturePostHogEvent(POST_HOG_EVENT.SIGN_UP.SUCCESS);
        }
      });
    } else {
      setUiState((prev) => ({ ...prev, hasTocError: !prev.hasTocError }));
      capturePostHogEvent(POST_HOG_EVENT.SIGN_UP.TERMS_NOT_ACCEPTED);
    }
  };

  return (
    <SignInUpWrapper
      title={t('auth_page.create_your_account')}
      isSelected={isSelected}
      showWelcomeUI={uiState.showWelcomeUI}>
      <BaseButtonLg
        title={t('auth_page.sign_up_with_email')}
        icon={<Mail className='~w-3.5/6 h-auto' fill={colors.WHITE} />}
        additionalStyles={`~min-w-44/96 w-fit ~px-5/10 py-1/2 self-center hover-scale ${!uiState.showWelcomeUI && 'hidden'}`}
        onClick={() => {
          capturePostHogEvent(POST_HOG_EVENT.SIGN_UP.START_EMAIL_FLOW_CLICKED);
          setUiState((prev) => ({
            ...prev,
            showWelcomeUI: !prev.showWelcomeUI
          }));
        }}
        data-testid={ATTRIB.TEST.SIGN_UP_WITH_EMAIL}
      />
      <form
        className={`${uiState.showWelcomeUI ? 'hidden' : 'flex'} flex-col ~gap-4/5 animate-fadeIn flex`}
        onSubmit={(event) => {
          event.preventDefault();
          capturePostHogEvent(POST_HOG_EVENT.SIGN_UP.SUBMIT_CLICKED);
          handleSubmit();
        }}>
        <EmailInput
          value={email}
          onChange={({ target: { value } }) =>
            dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_EMAIL, value })
          }
          hasError={!isValidEmail && uiState.hasAttemptedSubmit}
          error={
            !isValidEmail && uiState.hasAttemptedSubmit
              ? t('accountability_buddy.invalid_email')
              : undefined
          }
          readOnly={isAnyAuthActionInProgress}
          data-testid={ATTRIB.TEST.INPUT_EMAIL_SIGN_UP}
        />
        <PasswordInput
          mode={PASSWORD_INPUT_MODE.REGISTER}
          hasAttemptedSubmit={uiState.hasAttemptedSubmit}
          isValidPassword={isValidPassword}
        />
        <PrivacyConsentControl showTocError={uiState.hasTocError} />
        <BaseButton
          type='submit'
          title={t('auth_page.sign_up')}
          additionalStyles='py-2 shadow-md'
          disabled={isProcessing}
          isLoading={isProcessing}
          data-testid={ATTRIB.TEST.SIGN_UP_SUBMIT}
        />
        <div className='flex items-center gap-2 ~py-1/2'>
          <p className='min-w-max ~text-[0.65rem]/sm text-gray-700 dark:text-secondary-dark-700'>
            {t('auth_page.already_have_an_account')}
          </p>
          <BaseButton
            variant='link'
            title={t('auth_page.sign_in')}
            onClick={() => {
              dispatch?.({ type: AUTH_ACTION_TYPE.NAVIGATE_TO_SIGN_IN });
              setUiState((prev) => ({ ...prev, showWelcomeUI: true }));
            }}
            additionalStyles='min-w-max'
            disabled={isAnyAuthActionInProgress || isProcessing}
          />
        </div>
      </form>
    </SignInUpWrapper>
  );
};

export default SignUp;
